import type { ExternalChatConfig } from '@/services/external-chat/types';
import type {
  ExternalChatFilterDef,
  ExternalChatFilterType,
} from '@/types/source-types';

/**
 * Map admin-schema filter types onto the embed FilterBar's internal
 * vocabulary. The admin schema calls date ranges `range`; the embed
 * has historically used `date_range`. Aligning here — rather than in
 * every consumer — keeps the embed contract stable as the admin
 * schema evolves.
 */
const ADMIN_TO_EMBED_TYPE: Record<ExternalChatFilterType, string> = {
  select: 'select',
  multiselect: 'multiselect',
  text: 'text',
  range: 'date_range',
};

const normalizeFilterType = (type: ExternalChatFilterType): string =>
  ADMIN_TO_EMBED_TYPE[type] ?? type;

interface BuildEmbedParamsArgs {
  config: Partial<ExternalChatConfig>;
  apiKey: string;
  collectionName: string;
  /**
   * Filters from `collection.external_chat_filters`. Serialized as a
   * compact `filters` URL param with only `{field, label, type}` — the
   * iframe fetches live values from Qdrant via the embed-key-authed
   * facet endpoint so stale values never leak into the URL.
   */
  externalChatFilters?: ExternalChatFilterDef[] | null;
}

/**
 * Build URLSearchParams for the embed iframe URL from a config snapshot.
 * Omits values that match their defaults to keep URLs short.
 */
export function buildEmbedSearchParams({
  config,
  apiKey,
  collectionName,
  externalChatFilters,
}: BuildEmbedParamsArgs): URLSearchParams {
  const params = new URLSearchParams({
    apiKey,
    primaryColor: config.primary_color || '#2563eb',
  });

  if (config.font_color && config.font_color !== '#111827') {
    params.set('fontColor', config.font_color);
  }
  if (config.background_color && config.background_color !== '#ffffff') {
    params.set('bgColor', config.background_color);
  }
  if (config.link_color && config.link_color !== '#2563eb') {
    params.set('linkColor', config.link_color);
  }
  if (config.greeting) params.set('greeting', config.greeting);
  params.set('title', config.title || collectionName || 'Chat');

  // Serialize filters with only {field,label,type} — iframe loads live
  // facet hits on mount, so stale URL-baked options never leak through.
  if (externalChatFilters && externalChatFilters.length > 0) {
    const sanitized = externalChatFilters
      .filter((f) => f.field)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((f) => ({
        field: f.field,
        label: f.label,
        type: normalizeFilterType(f.type),
      }));
    if (sanitized.length > 0) {
      params.set('filters', JSON.stringify(sanitized));
    }
  }

  if (config.header_font) params.set('headerFont', config.header_font);
  if (config.header_font_weight && config.header_font_weight !== '_default') {
    params.set('headerFontWeight', config.header_font_weight);
  }
  if (config.body_font) params.set('bodyFont', config.body_font);
  if (config.body_font_weight && config.body_font_weight !== '_default') {
    params.set('bodyFontWeight', config.body_font_weight);
  }
  if (config.search_bg_color) {
    params.set('searchBgColor', config.search_bg_color);
  }
  if (config.search_text_color) {
    params.set('searchTextColor', config.search_text_color);
  }
  if (config.custom_css) params.set('customCss', config.custom_css);
  if (config.border_radius) params.set('borderRadius', config.border_radius);
  if (config.content_max_width && config.content_max_width !== '672px') {
    params.set('contentMaxWidth', config.content_max_width);
  }

  return params;
}

export function getEmbedPath(config: Partial<ExternalChatConfig>): string {
  return config.embed_type === 'search' ? 'search' : 'chat';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

interface BuildEmbedSnippetArgs {
  config: Partial<ExternalChatConfig>;
  activeKey: string;
  collectionName: string;
  orgId: string;
  collectionId: string;
  externalChatFilters?: ExternalChatFilterDef[] | null;
}

/**
 * Build the iframe HTML snippet users paste into their site.
 * All interpolated values are HTML-encoded to prevent XSS.
 */
export function buildEmbedSnippet({
  config,
  activeKey,
  collectionName,
  orgId,
  collectionId,
  externalChatFilters,
}: BuildEmbedSnippetArgs): string {
  const params = buildEmbedSearchParams({
    config,
    apiKey: activeKey || 'YOUR_API_KEY',
    collectionName,
    externalChatFilters,
  });
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://app.capitol.ai';
  const embedPath = getEmbedPath(config);
  const titleValue = config.title || collectionName || 'Chat';
  const borderRadius = config.border_radius || '8px';
  const embedHeight = config.embed_height || '600px';
  const src = `${origin}/embed/${embedPath}/${orgId}/${collectionId}?${params.toString()}`;

  return `<iframe
  src="${escapeHtml(src)}"
  style="width: 100%; height: ${escapeHtml(embedHeight)}; border: none; border-radius: ${escapeHtml(borderRadius)};"
  title="${escapeHtml(titleValue)}"
  allow="clipboard-write"
></iframe>`;
}

interface BuildPreviewUrlArgs {
  config: Partial<ExternalChatConfig>;
  activeKey: string;
  collectionName: string;
  orgId: string;
  collectionId: string;
  externalChatFilters?: ExternalChatFilterDef[] | null;
}

/**
 * Build the relative URL loaded into the admin live-preview iframe.
 */
export function buildPreviewUrl({
  config,
  activeKey,
  collectionName,
  orgId,
  collectionId,
  externalChatFilters,
}: BuildPreviewUrlArgs): string {
  if (!activeKey) return '';
  const params = buildEmbedSearchParams({
    config,
    apiKey: activeKey,
    collectionName,
    externalChatFilters,
  });
  const embedPath = getEmbedPath(config);
  return `/embed/${embedPath}/${orgId}/${collectionId}?${params.toString()}`;
}
